import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';

import { useConfigurations } from '@/features/_data/useConfigurations';
import { Button } from '@/ui/Button/Button';
import { Flex } from '@/ui/Flex/Flex';
import { Input } from '@/ui/Input/Input';
import { Modal } from '@/ui/Modal/Modal';
import { useSelectedCustomerId } from '@/ui/Navbar/Navbar';
import { fetcher } from '@/utils/fetcher';
import { useUnitData } from '@/features/_data/useUnitData';
import { TOASTTYPE, useToast } from '@/ui/Toast/Toast';
import { RETAIL_API_URL } from 'src/utils/envConfig';
import { useIntl } from 'react-intl';

interface Props {
  isModalOpen: boolean;
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  unitId: string | undefined;
}

const StyledSaveButton = styled(Button)`
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  align-self: flex-end;
  cursor: pointer;

  ${({ disabled }) =>
    disabled &&
    css`
      background-color: ${({ theme }) => theme.color('#B2B1BA')};
      pointer-events: none;
    `}
`;

export const EditUnitModal = ({
  isModalOpen,
  setIsModalOpen,
  unitId,
}: Props) => {
  const [selectedCustomerId] = useSelectedCustomerId();
  const [currentUnitName, setCurrentUnitName] = useState<string>('');

  const { refetch: refetchConfigurations } =
    useConfigurations(selectedCustomerId);

  const toast = useToast();
  const intl = useIntl();
  const { data, refetch: refetchUnitData } = useUnitData(unitId);

  const updatedUnit = {
    ...data,
    name: currentUnitName,
  };

  useEffect(() => {
    if (isModalOpen && data?.name) {
      setCurrentUnitName(data?.name);
    }
  }, [isModalOpen, data?.name]);

  const handleUpdateUnit = async () => {
    if (!navigator.onLine) {
      toast({
        subtitle: intl.formatMessage({
          defaultMessage: 'Please connect to the internet to save the name.',
          id: 'qjoy94',
        }),
        mode: TOASTTYPE.No_Connection,
      });
      setIsModalOpen(false);
      return;
    }

    if (data?.name === currentUnitName) {
      toast({
        title: intl.formatMessage({
          defaultMessage: 'No changes detected.',
          id: 'SnE6J3',
        }),
        subtitle: intl.formatMessage({
          defaultMessage: 'The name was not changed.',
          id: 'fkA23P',
        }),
        mode: TOASTTYPE.Info,
      });
      setIsModalOpen(false);
      return;
    }

    try {
      await fetcher(`${RETAIL_API_URL}/units/update`, {
        method: 'POST',
        body: JSON.stringify(updatedUnit),
      });

      toast({
        subtitle: intl.formatMessage({
          defaultMessage: 'The name has been changed!',
          id: 'GxoPm0',
        }),
        mode: TOASTTYPE.Success,
      });

      setIsModalOpen(false);
      refetchConfigurations();
      refetchUnitData();
    } catch (error) {
      toast({
        title: intl.formatMessage({
          defaultMessage: 'Error! Something went wrong changing the name.',
          id: 'K26oQc',
        }),
        mode: TOASTTYPE.Danger,
      });
      setIsModalOpen(false);
    }
  };

  return isModalOpen && data?.name ? (
    <Modal onClose={() => setIsModalOpen(false)} title="Edit Unit Name">
      <Flex gap={16} direction="column">
        <Input
          onChange={(value) => setCurrentUnitName(value)}
          placeholder="Name"
          value={currentUnitName}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              handleUpdateUnit();
            }
          }}
        />
        <StyledSaveButton
          disabled={!currentUnitName.trim()}
          onClick={handleUpdateUnit}
        >
          Save
        </StyledSaveButton>
      </Flex>
    </Modal>
  ) : null;
};
